import { Link as RouterLink, useNavigate } from 'react-router-dom'

import { clsx } from 'clsx'

import {
  Heading,
  Text,
  Link,
  Button,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { Markdown } from '~publish/components/Markdown'
import { TemplateCard } from '~publish/components/TemplateCard'

import { getCampaignBranding } from './campaignBranding'
import type { Template, TemplateCampaign } from '../../types'
import {
  getCappedCurrentDayNumber,
  isDripCampaign,
  pickCampaignBannerTemplate,
} from '../utils'

import styles from './CampaignBanner.module.css'

import type { JSX } from 'react'

export const CampaignBanner = ({
  campaign,
  templates,
  onFilter,
  preview = false,
  titleAs = 'h2',
}: {
  campaign: TemplateCampaign
  templates: Template[]
  onFilter: () => void
  /**
   * Heading level for the campaign title. Pass `h3` when the banner sits
   * inside a section that already has its own `h2`.
   */
  titleAs?: 'h2' | 'h3'
  /**
   * When true, the banner renders in a non-interactive preview mode:
   * CTAs are disabled and the template card is not linked. Used by the
   * CampaignForm live preview so clicks don't navigate away from the form.
   */
  preview?: boolean
}): JSX.Element | null => {
  const navigate = useNavigate()

  const template = pickCampaignBannerTemplate(campaign, templates)
  if (!template) return null

  const branding = getCampaignBranding(campaign)
  const showDayBadge = isDripCampaign(campaign)

  const handleUsePrompt = (): void => {
    navigate({
      pathname: `/create/templates/template/${template.id}`,
      search: `cta=create-templates-use-prompt`,
    })
  }

  return (
    <div
      className={clsx(styles.container, {
        [styles[campaign.bannerColor ?? 'blue']]: true,
        [styles.customColor]: branding?.stampedBorder,
        [styles.darkTheme]: branding?.forceDarkMode,
      })}
    >
      <div className={styles.content}>
        <aside className={styles.aside}>
          {branding ? (
            <div className={styles.brandedTitle}>
              <VisuallyHidden as={titleAs}>{branding.logoLabel}</VisuallyHidden>
              <branding.Logo />
              {branding.tagline && (
                <Text className={styles.brandedTagline} size="lg">
                  {branding.tagline}
                </Text>
              )}
            </div>
          ) : (
            <Heading as={titleAs} size="xl">
              {campaign.title}
            </Heading>
          )}

          <div className={styles.description}>
            {showDayBadge ? (
              <Text color="subtle">
                Day {getCappedCurrentDayNumber(campaign)} Prompt
              </Text>
            ) : (
              <Text color="subtle">
                {campaign.heading || 'Prompt of the day'}
              </Text>
            )}
            <div>
              {campaign.description && (
                <Markdown className={styles.descriptionMarkdown}>
                  {campaign.description}
                </Markdown>
              )}
              {branding?.promoLink && (
                <>
                  {' '}
                  <Text size="lg">
                    {branding.promoLink.trailingText}
                    <Link
                      color="inherit"
                      external
                      href={branding.promoLink.href}
                    >
                      {branding.promoLink.label}
                    </Link>
                    .
                  </Text>
                </>
              )}
              {/* Custom Discord CTA for Comment Score Sprint Challenge, can be removed end of April 2026 */}
              {campaign.id === 'TC-4' && (
                <Text as="p" size="lg">
                  <Link
                    color="inherit"
                    external
                    href="https://buffer.com/our-community/"
                  >
                    Join the fun in Discord!
                  </Link>
                </Text>
              )}
            </div>
          </div>
          <footer className={styles.actions}>
            <Button onClick={preview ? undefined : handleUsePrompt}>
              {isDripCampaign(campaign) ? 'Start Posting' : 'Use Prompt'}
            </Button>
            <Button
              variant="secondary"
              onClick={preview ? undefined : onFilter}
            >
              See All
            </Button>
          </footer>
        </aside>

        <div
          className={clsx({
            [styles.stampedBorder]: branding?.stampedBorder,
          })}
        >
          {branding?.stampedBorder && <hr />}
          <TemplateCard template={template} className={styles.template}>
            {!preview && (
              <TemplateCard.Clickable
                asChild
                id={`template-card-campaignBanner-${template.id}`}
              >
                <RouterLink
                  to={{
                    pathname: `/create/templates/template/${template.id}`,
                    search: `cta=create-templates-campaignBanner-template-1&trigger=template-card-campaign-${template.id}`,
                  }}
                  aria-label={`Open template ${template.title}`}
                />
              </TemplateCard.Clickable>
            )}
            <TemplateCard.Icon />
            <TemplateCard.Title as={titleAs === 'h2' ? 'h3' : 'h4'} />
            <TemplateCard.Description />
            <TemplateCard.Tags />
          </TemplateCard>
        </div>
      </div>
    </div>
  )
}
